import type {
  GetFormFieldOptionsByParentTypeQuery,
  Parent_Type_Enum,
} from '@risksmart-app/web-graphql-client/generated/graphql';
import _ from 'lodash';
import { useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import type { ZodSchema } from 'zod';
import { z } from 'zod';

import { useConditionalFieldsContext } from '../../conditional-fields-provider/ConditionalFieldsContext';
import { useCustomisableFormDataContext } from '../../customisable-form-data/CustomisableFormDataContext';
import { useRiskSmartForm } from '../RiskSmartFormContext';

const checkRequired = (val: unknown) => {
  if (Array.isArray(val)) {
    return val.length > 0;
  }
  if (typeof val === 'string') {
    return val.length > 0;
  }

  return !_.isNil(val);
};

export const getRequiredFields = ({
  allFieldIds,
  defaultRequiredFields,
  fieldConfig,
  conditionallyHiddenFields,
}: {
  allFieldIds: string[];
  defaultRequiredFields: string[];
  fieldConfig: GetFormFieldOptionsByParentTypeQuery['form_field_configuration'];
  conditionallyHiddenFields: Set<string>;
}) => {
  const requiredFields = new Set<string>();

  // Use default required unless overridden with user settings
  defaultRequiredFields
    .filter((fieldId) => !fieldConfig.map((f) => f.FieldId).includes(fieldId))
    .forEach((fieldId) => requiredFields.add(fieldId));

  fieldConfig
    .filter(
      (field) =>
        allFieldIds.includes(field.FieldId) &&
        field.Required &&
        !conditionallyHiddenFields.has(field.FieldId)
    )
    .forEach((field) => requiredFields.add(field.FieldId));

  return requiredFields;
};

export const addRequireFieldsToSchema = (
  schema: ZodSchema,
  requiredFields: Set<string>
): ZodSchema => {
  if (requiredFields.size === 0) {
    return schema;
  }

  // Run required-field checks and the base schema in parallel, then merge
  // their errors. We can't use z.intersection because it would merge the two
  // parsed outputs, which conflicts with .pipe() transforms that change a
  // field's type (e.g. string→number). We can't use schema.superRefine
  // because when the base schema aborts (e.g. a field whose value has the
  // wrong type, like null where an object is expected) the refinement never
  // runs — which would suppress required errors on other fields.
  return z
    .any()
    .superRefine((input, ctx) => {
      // Run base schema first so its (usually more specific) errors appear
      // before the generic "Required" errors for the same field path.
      const result = schema.safeParse(input);
      const pathsWithBaseErrors = new Set<string>();
      if (!result.success) {
        for (const issue of result.error.issues) {
          ctx.addIssue(issue);
          pathsWithBaseErrors.add(issue.path.join('.'));
        }
      }
      for (const fieldId of requiredFields) {
        // Skip if the base schema already reported an error on this field —
        // its message is more specific than a generic "Required".
        if (pathsWithBaseErrors.has(fieldId)) {
          continue;
        }
        if (!checkRequired(_.get(input, fieldId))) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Required',
            path: fieldId.split('.'),
            fatal: true,
          });
        }
      }
    })
    .transform((input) => {
      // Return the base schema's transformed output (e.g. coerced numbers
      // from .pipe() transforms) so consumers like react-hook-form receive
      // the correctly typed form values.
      const result = schema.safeParse(input);

      return result.success ? result.data : input;
    });
};

export const useTransformSchemaToRequiredFields = (
  parentType: Parent_Type_Enum
) => {
  const conditionallyHiddenFields = useConditionalFieldsContext();
  const { setCustomFormValidation, defaultRequiredFields, allFieldIds } =
    useRiskSmartForm();
  const { formFieldConfigurations } = useCustomisableFormDataContext();

  const { control } = useFormContext();

  useEffect(() => {
    setCustomFormValidation(
      (): ((schema: ZodSchema) => ZodSchema) => (schema) => {
        const requiredFields = getRequiredFields({
          allFieldIds,
          defaultRequiredFields,
          fieldConfig: formFieldConfigurations ?? [],
          conditionallyHiddenFields,
        });

        return addRequireFieldsToSchema(schema, requiredFields);
      }
    );
  }, [
    formFieldConfigurations,
    setCustomFormValidation,
    defaultRequiredFields,
    control,
    allFieldIds,
    parentType,
    conditionallyHiddenFields,
  ]);
};
